import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    return Math.ceil((t - Date.now()) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

export default function Subscription({ restaurant }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const subscribe = (plan) => {
    if (!plan.payment_link) {
      toast.error("Link di pagamento non configurato per questo piano. Contatta il supporto.");
      return;
    }
    const params = new URLSearchParams();
    if (restaurant?.id) params.set("client_reference_id", restaurant.id);
    if (user?.email) params.set("prefilled_email", user.email);
    // Redirect to Stripe-hosted Payment Link
    window.location.href = `${plan.payment_link}?${params.toString()}`;
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  const currentPlan = restaurant?.subscription_plan;
  const status = restaurant?.subscription_status;
  const trialDays = status === "trialing" ? daysUntil(restaurant?.trial_ends_at) : null;
  const trialExpired = status === "trialing" && trialDays !== null && trialDays <= 0;

  const statusLabel =
    trialExpired ? "Periodo di prova terminato" :
    status === "active" ? "Attivo" :
    status === "trialing" ? `Periodo di prova (${trialDays}g rimasti)` :
    status === "canceled" ? "Annullato" : status;

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Abbonamento</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Il tuo piano</h1>
      </div>

      {trialExpired && (
        <div
          className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-sm"
          data-testid="trial-expired-banner"
        >
          Il tuo periodo di prova è terminato. <strong>Attiva un piano</strong> qui sotto per riprendere a ricevere ordini e gestire il menu.
        </div>
      )}
      {status === "trialing" && !trialExpired && trialDays !== null && trialDays <= 3 && (
        <div
          className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm"
          data-testid="trial-warning-banner"
        >
          Il tuo periodo di prova finisce tra <strong>{trialDays} giorno/i</strong>. Attiva un piano per continuare senza interruzioni.
        </div>
      )}

      <div className="p-6 bg-card border border-border rounded-2xl flex flex-col md:flex-row justify-between md:items-center gap-4" data-testid="subscription-status">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Stato attuale</div>
          <div className="font-display text-2xl font-bold mt-1">{statusLabel}</div>
        </div>
        <div className="flex-1 md:text-right">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Piano</div>
          <div className="font-display text-2xl font-bold mt-1">{currentPlan || "—"}</div>
        </div>
        {status === "active" && restaurant?.stripe_subscription_id && (
          <Button
            onClick={openPortal}
            disabled={portalLoading}
            className="pill-btn bg-primary text-primary-foreground hover:bg-[hsl(var(--accent))]"
            data-testid="manage-subscription-btn"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            {portalLoading ? "…" : "Gestisci abbonamento"}
            <ExternalLink className="h-3 w-3 ml-2" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((p, idx) => {
          const isCurrent = currentPlan === p.lookup_key && status === "active";
          const popular = idx === 1;
          return (
            <div key={p.id} className={`relative p-6 rounded-2xl border ${popular ? "border-[hsl(var(--accent))]" : "border-border"} bg-card`} data-testid={`sub-plan-${p.lookup_key}`}>
              {popular && <span className="absolute -top-2 left-4 text-[10px] uppercase tracking-widest bg-[hsl(var(--accent))] text-white px-2 py-0.5 rounded-full">Popolare</span>}
              <div className="font-display text-xl font-bold">{p.name}</div>
              <div className="font-display text-4xl font-black tracking-tighter mt-2">
                €{(p.price_cents / 100).toFixed(0)}
                <span className="text-sm font-normal text-muted-foreground">/mese</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2"><Check className="h-4 w-4 text-[hsl(var(--accent))]" /> {f}</li>
                ))}
              </ul>
              <Button
                onClick={() => subscribe(p)}
                disabled={isCurrent}
                className={`w-full mt-6 pill-btn ${isCurrent ? "bg-emerald-600 text-white" : "bg-primary text-primary-foreground hover:bg-[hsl(var(--accent))]"}`}
                data-testid={`subscribe-btn-${p.lookup_key}`}
              >
                {isCurrent ? "Piano attivo" : "Abbonati"}
                {!isCurrent && <ExternalLink className="h-3 w-3 ml-2" />}
              </Button>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Il pagamento è gestito in modo sicuro da Stripe. Verrai reindirizzato al checkout ufficiale.
      </p>
    </div>
  );
}

